import { getSubmissionVerdictKey } from './getSubmissionVerdictKey';
import { resolveSubmissionEarningsDisplay } from './resolveSubmissionEarningsDisplay';

export type CampaignEarningsSubmission = {
  disqualified?: boolean | null;
  admin_approval?: string | null;
  payable_views?: number | null;
  latest_views?: number | null;
  last_counted_views?: number | null;
  accumulated_payout_cents?: number | null;
  cpm_rate_override_cents?: number | null;
};

export type CampaignEarningsCampaign = {
  cpm_rate_cents: number;
  min_views_threshold?: number | null;
  max_payout_per_clip_cents?: number | null;
};

export type CampaignEarningsSummary = {
  /** Submissions that can still earn (disapproved/disqualified excluded). */
  clipCount: number;
  totalViews: number;
  /** Already-credited payouts on approved submissions. */
  confirmedCents: number;
  /** Estimates on submissions still awaiting review. */
  potentialCents: number;
};

/**
 * Campaign-scoped rollup of the per-clip figures the submissions table already
 * shows, so the header can state "what am I making here" without the reader
 * summing the rows themselves. Every clip goes through
 * `resolveSubmissionEarningsDisplay`, which is what keeps the total honest
 * against the rows below it — thresholds, per-clip caps and rate overrides
 * included.
 */
export function summarizeCampaignEarnings(
  submissions: CampaignEarningsSubmission[],
  campaign: CampaignEarningsCampaign,
): CampaignEarningsSummary {
  return submissions.reduce<CampaignEarningsSummary>(
    (acc, s) => {
      const verdict = getSubmissionVerdictKey(s);
      if (verdict === 'disapproved' || verdict === 'disqualified') return acc;

      const views = Number(s.payable_views ?? s.latest_views ?? s.last_counted_views ?? 0);
      const safeViews = Number.isFinite(views) ? Math.max(0, views) : 0;
      const earnings = resolveSubmissionEarningsDisplay({
        verdict,
        views: safeViews,
        accumulatedPayoutCents: s.accumulated_payout_cents,
        campaignCpmRateCents: campaign.cpm_rate_cents,
        overrideCents: s.cpm_rate_override_cents,
        minViewsThreshold: campaign.min_views_threshold,
        maxPayoutPerClipCents: campaign.max_payout_per_clip_cents,
      });

      return {
        clipCount: acc.clipCount + 1,
        totalViews: acc.totalViews + safeViews,
        confirmedCents: acc.confirmedCents
          + (earnings.kind === 'confirmed' ? earnings.cents : 0),
        potentialCents: acc.potentialCents
          + (earnings.kind === 'potential' ? earnings.cents : 0),
      };
    },
    { clipCount: 0, totalViews: 0, confirmedCents: 0, potentialCents: 0 },
  );
}
